// Products Admin Section - Admin interface for managing products
// Follows the Section, Component & Manager System architecture

import React, { useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../../../components/Layout';
import { MarketplaceManager } from '../../managers/products-manager';
import { useManager } from '../../../../core';
import { useAuth } from '../../../../contexts/AuthContext';
import { ProductsLayout, ProductSearchBar, ProductGrid, ProductPagination, MarketplaceLoading } from '../../components';
import { RevoltSwitch } from '../../../../components/RevoltSwitch';
import { eventUtils } from '../../../../core/utils';
import { ProductModal } from './ProductModal';
import './ProductsAdmin.css';

export function ProductsAdminSection() {
  const { user, loading: authLoading } = useAuth();
  const { manager, isInitialized } = useManager(MarketplaceManager);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Local state for immediate input responsiveness
  const [localSearchTerm, setLocalSearchTerm] = useState('');

  // Local state for current search term (triggers re-renders)
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  const [searchResults, setSearchResults] = useState([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  // Modal state
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);

  // Reorder version - increment to force re-render after featured reorder
  const [reorderVersion, setReorderVersion] = useState(0);

  // Show inactive products toggle (default off)
  const [showInactive, setShowInactive] = useState(false);

  // Get initial search term from URL parameters
  const urlQuery = searchParams.get('query') || '';

  // Initialize local state from URL on mount (only time input gets set externally)
  const hasInitializedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasInitializedRef.current && urlQuery) {
      hasInitializedRef.current = true;
      setLocalSearchTerm(urlQuery);
      setCurrentSearchTerm(urlQuery);
    }
  }, [urlQuery]);

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [currentSearchTerm]);

  const shouldShowSearchResults = currentSearchTerm.trim().length > 0;

  const executeSearch = useCallback(async (query, page = 1) => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) {
      setSearchResults([]);
      setSearchTotal(0);
      return;
    }

    setIsSearchLoading(true);
    const filters = showInactive ? { includeInactive: true } : {};
    const response = await manager.searchProductsFromAPI(normalizedQuery, page, itemsPerPage, filters);
    setSearchResults(response.products || []);
    setSearchTotal(response.total || 0);
    setIsSearchLoading(false);
  }, [manager, itemsPerPage, showInactive]);

  // Function to perform actual search (called on Enter or button click)
  const performSearch = useCallback(async (query) => {
    const processedQuery = query.trim();
    setCurrentSearchTerm(processedQuery);
    setCurrentPage(1);

    if (!processedQuery) {
      setSearchResults([]);
      setSearchTotal(0);
      return;
    }

    await executeSearch(processedQuery, 1);
  }, [executeSearch]);

  // Debounced function to update URL from typing (250ms delay)
  const updateUrlFromTyping = useCallback((query) => {
    // Update URL with the exact query as entered (no processing for URL storage)
    const newParams = new URLSearchParams(searchParams);
    if (query) {
      // Store exactly what was typed (URLSearchParams will encode spaces as %20)
      newParams.set('query', query);
    } else {
      newParams.delete('query');
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Create debounced version of URL update function (250ms delay)
  const debouncedUrlUpdateRef = React.useRef(null);

  // Update the debounced function when updateUrlFromTyping changes
  React.useEffect(() => {
    debouncedUrlUpdateRef.current = eventUtils.debounce(updateUrlFromTyping, 250);
  }, [updateUrlFromTyping]);

  // Helper function to call the debounced URL update
  const debouncedUrlUpdate = React.useCallback((...args) => {
    if (debouncedUrlUpdateRef.current) {
      debouncedUrlUpdateRef.current(...args);
    }
  }, []);

  // Handle immediate input changes (for responsive typing)
  const handleSearchChange = useCallback((newTerm) => {
    // Update local state immediately for responsive UI
    setLocalSearchTerm(newTerm);
    // Debounce URL updates (250ms delay)
    debouncedUrlUpdate(newTerm);
  }, [debouncedUrlUpdate]);

  // If page loads with a URL query, run backend search once initialized; re-run when showInactive changes
  React.useEffect(() => {
    if (!isInitialized) return;
    if (!currentSearchTerm.trim()) return;
    executeSearch(currentSearchTerm, currentPage);
  }, [isInitialized, currentSearchTerm, currentPage, executeSearch, showInactive]);

  const featuredProducts = manager.getFeaturedProducts();
  const featuredStartIndex = (currentPage - 1) * itemsPerPage;
  const featuredEndIndex = featuredStartIndex + itemsPerPage;

  const displayedProducts = shouldShowSearchResults
    ? searchResults
    : featuredProducts.slice(featuredStartIndex, featuredEndIndex);

  const totalItems = shouldShowSearchResults ? searchTotal : featuredProducts.length;

  // Handle page changes
  const handlePageChange = React.useCallback((page) => {
    setCurrentPage(page);
    if (shouldShowSearchResults) {
      executeSearch(currentSearchTerm, page);
    }
    // Scroll to top of results when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [shouldShowSearchResults, currentSearchTerm, executeSearch]);

  // Handle product click - navigate to admin product details
  const handleProductClick = useCallback((product) => {
    const params = new URLSearchParams();
    params.set('productId', product.id);
    navigate(`/marketplace/admin/details?${params.toString()}`);
  }, [navigate]);

  // Handle show inactive toggle - reload featured products with/without inactive
  const handleShowInactiveChange = useCallback(async (checked) => {
    setShowInactive(checked);
    manager.setIncludeInactive(checked);
    await manager.loadContent();
    setReorderVersion((v) => v + 1);
  }, [manager]);

  // Handle featured products reorder (admin, featured grid only)
  const handleReorder = useCallback(async (swaps) => {
    try {
      await manager.reorderFeaturedProducts(swaps);
      setReorderVersion((v) => v + 1);
    } catch (error) {
      console.error('Failed to reorder featured products:', error);
    }
  }, [manager]);

  // Modal handlers - refresh products after create, then navigate to details
  const handleProductModalSubmit = useCallback(async (createdProduct) => {
    await manager.loadContent();
    setReorderVersion((v) => v + 1);
    if (createdProduct?.id) {
      const params = new URLSearchParams();
      params.set('productId', createdProduct.id);
      navigate(`/marketplace/admin/details?${params.toString()}`);
    }
  }, [manager, navigate]);

  // Check if manager is initialized
  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--products">
        <MarketplaceLoading
          title="Loading Marketplace Admin"
          subtitle="Setting up admin controls..."
        />
      </MainLayout>
    );
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <MainLayout contentClassName="main-content--products">
        <MarketplaceLoading
          title="Verifying Access"
          subtitle="Checking admin permissions..."
        />
      </MainLayout>
    );
  }

  // Check admin access - only show admin panel for admins
  if (!user || user.role !== 'admin') {
    return (
      <MainLayout contentClassName="main-content--products">
        <div className="products-section">
          <div className="access-denied">
            <i className="fa-solid fa-shield-alt" style={{ fontSize: '3rem', color: '#ff4444', marginBottom: '20px' }}></i>
            <h1>Access Denied</h1>
            <p>This admin section is only available to administrators.</p>
            <p>Please contact support if you believe this is an error.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout contentClassName="main-content--products">
      <div className="products-section">
        {/* Root Layout: Sidebar + Main Content Area */}
        <ProductsLayout
          sidebar={
            <div className="products-sidebar-content">
              <div className="products-sidebar-filters">
                <h3 className="products-sidebar-section-title">Filters</h3>
                <p className="products-sidebar-filters-placeholder">Sidebar content will go here...</p>
                {/* Future: Price range slider, category filters, tag filters */}
              </div>
            </div>
          }
          content={
            <div className="products-main-content">
              {/* Title + Search Bar Row - Back to Products left, Create Product + Show Inactive right */}
              <div className="products-controls-row">
                <button
                  className="products-admin-btn products-back-btn"
                  onClick={() => navigate('/marketplace')}
                  title="Back to Marketplace"
                >
                  <i className="fa-solid fa-arrow-left"></i>
                  Back to Marketplace
                </button>
                <div className="products-title-inline">Marketplace Admin</div>
                <ProductSearchBar
                  searchTerm={localSearchTerm}
                  onSearchChange={handleSearchChange}
                  onSubmit={performSearch}
                  placeholder="Search products..."
                />
                <div className="products-admin-nav-right">
                  <button
                    className="products-admin-btn products-create-btn"
                    onClick={() => setIsProductModalOpen(true)}
                    title="Create New Product"
                  >
                    <i className="fa-solid fa-plus"></i>
                    Create Product
                  </button>

                  <RevoltSwitch
                    checked={showInactive}
                    onChange={handleShowInactiveChange}
                    label="Show inactive"
                    className="products-admin-show-inactive"
                  />
                </div>
              </div>

              {/* Pagination Controls - Only shows if more than 25 items */}
              <ProductPagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />

              {/* Products Grid Area - Takes remaining vertical space */}
              <div className="products-grid-area">
                <ProductGrid
                  key={reorderVersion}
                  products={displayedProducts}
                  onProductClick={handleProductClick}
                  loading={shouldShowSearchResults ? isSearchLoading : manager.isLoading}
                  emptyMessage={
                    shouldShowSearchResults
                      ? "No products match your search"
                      : "No featured products available"
                  }
                  admin={true}
                  draggable={!shouldShowSearchResults}
                  onReorder={handleReorder}
                  orderBaseIndex={shouldShowSearchResults ? 0 : featuredStartIndex}
                />
              </div>
            </div>
          }
        />
      </div>

      {/* Product Modal */}
      <ProductModal
        isOpen={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        onSubmit={handleProductModalSubmit}
      />
    </MainLayout>
  );
}